import {
  useInfiniteQuery,
  useQuery,
  type QueryKey,
  type InfiniteData
} from "@tanstack/react-query";
import { useCallback } from "react";
import { GetLeaderboardClusterData, ListGetLeaderboardClusterData } from "./leaderboard.types";
import { ERRORS } from "../consts";
import {
  apiAdapter,
  fetcherFactory } from "../services";
import { useAuthStore, useLeaderboardClusterStore, useLanguageStore } from "../stores";
import type { LeaderboardResponse } from "../types";
import { getEnvironmentVariables } from "../utils/general";

const { basePath } = getEnvironmentVariables();

export function useLeaderboardClustersQuery({
  enabled=false
}: {enabled?:boolean}) {
  const accessToken = useAuthStore(state => state.session?.getAccessToken().getJwtToken()) ?? "";
  const corporateId = useAuthStore(state => state.userData?.organization_id) ?? "";
  const initiativeId = useAuthStore(state => state.userData?.initiative_id) ?? "";
  const setUnauthorized = useAuthStore(state => state.setUnAuthorized);
  const sessionToken = useAuthStore(state => state.sessionToken) ?? "";
  const isSeedingSession = useAuthStore(state => state.hasSeedSession);
  const fetcher = fetcherFactory();
  const isEnabled = Boolean(
    enabled
    && initiativeId
    && sessionToken
    && corporateId
  ) && !isSeedingSession;
  return useQuery<unknown, unknown, ListGetLeaderboardClusterData, QueryKey>({
    enabled: isEnabled,
    queryKey: ["useLeaderboardClustersQuery", corporateId, initiativeId],
    queryFn: useCallback(async () => {
      const pageSize = 100;
      let page = 0;
      let allItems: GetLeaderboardClusterData[] = [];
      let totalPages = 1;
      try {
        while(page<totalPages) {
          const response = await fetcher(
            `${basePath}/usermgmt/${corporateId}/initiatives/${initiativeId}/leaderboard-clusters?page=${page}&page_size=${pageSize}`,
            {
              headers: {
                "accept": "application/json",
                "Authorization": accessToken,
                "x-ada-session-token": sessionToken
              }
            });
          const result : {
            metadata: {
              total_pages: number,
              total_elements: number
            },
            output: GetLeaderboardClusterData[]
          } = await response.json();
          allItems.push(...result.output);
          totalPages = result.metadata.total_pages;
          page++;
        }
        return {
          metadata: {
            total_pages: totalPages,
            total_elements: allItems.length
          },
          output: allItems
        };
      } catch(error) {
        if (error === ERRORS.UNAUTHORIZED) {
          setUnauthorized(true);
          throw error; // rethrow so that react query doesn't complain about undefined return value
        } else {
          throw error;
        }
      }
    }, [fetcher, accessToken, sessionToken, setUnauthorized, corporateId, initiativeId]),
    refetchOnMount: true,
    staleTime: 3 * 60 * 1000
  });
}

export function useLeaderboardQuery({
  enabled = true,
  pageSize=6
} : {
  enabled?: boolean
  pageSize?: number
}) {
  const accessToken = useAuthStore(state => state.session?.getAccessToken().getJwtToken()) ?? "";
  const corporateId = useAuthStore(state => state.userData?.organization_id) ?? "";
  const initiativeId = useAuthStore(state => state.userData?.initiative_id) ?? "";
  const clusterId = useLeaderboardClusterStore(state => state.currentCluster?.id)??"";
  const setUnauthorized = useAuthStore(state => state.setUnAuthorized);
  const sessionToken = useAuthStore(state => state.sessionToken) ?? "";
  const isSeedingSession = useAuthStore(state => state.hasSeedSession);
  const language = useLanguageStore(state => state.language)?.id ?? "";


  const fetcher = fetcherFactory();

  const uri = `/learning-catalogue/${corporateId}/${initiativeId}/${clusterId}/learnerLeaderboard`;

  const enabledQuery = Boolean(
    !!enabled
    && !!initiativeId
    && !!sessionToken
    && !!corporateId
    && !!clusterId
    && !!language
  ) && !isSeedingSession;

  return useInfiniteQuery<
  unknown,
    unknown,
    InfiniteData<LeaderboardResponse, { nextPage: number } | undefined>,
    QueryKey,
    { nextPage: number } | undefined
  >({
    initialPageParam: undefined,
    enabled: enabledQuery,
    getNextPageParam: (lastPage, pages) => {
      const nextPage = pages.length;
      const totalPages = ((lastPage as LeaderboardResponse)?.metadata)?.totalPages || 0;
      const res = (nextPage < totalPages) ? { nextPage } : undefined;
      return res;
    },
    queryFn: ({ pageParam }) => {
      if (!enabledQuery) {
        return Promise.reject(new Error("Query learnerLeaderboard disabilitata"));
      }
      return fetcher(
        `${basePath}${uri}?page=${pageParam?.nextPage ?? 0}&page_size=${pageSize}`,
        {
          headers: {
            ...(language && { "Accept-Language": language }),
            "Authorization": accessToken,
            "x-ada-session-token": sessionToken
          }
        })
        .then((res) => res.json())
        .then((data: LeaderboardResponse) => ({
          callerRanking: data.callerRanking ? apiAdapter([data.callerRanking])[0] : null,
          leaderboards: apiAdapter(data.leaderboards ?? []),
          metadata: data.metadata
        }))
        .catch((error) => {
          if (error === ERRORS.UNAUTHORIZED) {
            setUnauthorized(true);
            throw error;
          } else {
            throw error;
          }
        });
    },
    queryKey: [accessToken, sessionToken, uri, isSeedingSession, pageSize, language],
    refetchOnMount: true,
    staleTime: 3 * 60 * 1000
  });
}


export function useGetPreviewWidgetItemsQuery({ enabled = true }: { enabled?: boolean } = {}) {
  const accessToken = useAuthStore(state => state.session?.getAccessToken().getJwtToken()) ?? "";
  const corporateId = useAuthStore(state => state.userData?.organization_id) ?? "";
  const initiativeId = useAuthStore(state => state.userData?.initiative_id) ?? "";
  const setUnauthorized = useAuthStore(state => state.setUnAuthorized);
  const sessionToken = useAuthStore(state => state.sessionToken) ?? "";
  const userId = useAuthStore(state => state.userData?.id_user) ?? "";
  const isSeedingSession = useAuthStore(state => state.hasSeedSession);
  const isLoading = useAuthStore(state => state.isLoading);
  const language = useLanguageStore(state => state.language)?.id;

  const fetcher = fetcherFactory();
  const uri = `/learning-catalogue/${corporateId}/${initiativeId}/redactional-activities`;

  return useQuery<
      unknown,
      unknown,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      any,
      QueryKey
    >({
      enabled: Boolean(
        enabled
        && userId
        && accessToken
        && sessionToken
        && corporateId
        && !isLoading
        && language
      ) && !isSeedingSession,
      queryFn: () => fetcher(
        `${basePath}${uri}`,
        {
          headers: {
            "Authorization": accessToken,
            "x-ada-session-token": sessionToken,
            ...(language && { "Accept-Language": language })
          },
          method: "GET"
        })
        .then((res) => {
          return res.json();
        })
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .then((data: {data:any[]}) => {
          return data;

        })
        .catch((error) => {
          if (error === ERRORS.UNAUTHORIZED) {
            setUnauthorized(true);
            throw error; // rethrow so that react query doesn't complain about undefined return value
          } else {
            throw error;
          }
        }),
      queryKey: [accessToken, sessionToken, uri, isSeedingSession,language],
      refetchOnMount: true,
      staleTime: 3 * 60 * 1000
    });
}

